import { config } from '@iheartjane/config';
import {
  Button,
  DesktopIcon,
  Flex,
  MobileIcon,
  contrastingColor,
  useDesktopMediaQuery,
  useToast,
} from '@iheartjane/reefer';
import type { MenuColorsConfig, MenuComponentConfig } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import { businessPaths, toBloomFontFamily } from '@jane/business-admin/util';
import { useGoogleFonts } from '@jane/shared-ecomm/data-access';

import { EditorPreview } from './components/menuEditor/EditorPreview';
import { EditorSidebar } from './components/menuEditor/EditorSidebar';
import type { SidebarSection } from './components/menuEditor/EditorSidebar';
import { ModalPageWrapper } from './components/ModalPageWrapper';

import styles from './PremiumMenuEditor.module.css';

const CONTRAST_OPTIONS = ['#0E0E0E', '#FFFFFF'];

const withContrast = (main: string) => ({
  main,
  contrast: contrastingColor(main, CONTRAST_OPTIONS),
});

export const buildThemeColors = (colors: MenuColorsConfig) => {
  const primary = withContrast(colors.primary?.main || '');

  return {
    primary,
    secondary: withContrast(colors.secondary?.main || ''),
    deals: withContrast(colors.deals?.main || ''),
    header: withContrast(colors.header?.main || ''),
    text: {
      primary_inverse: primary.contrast === '#0E0E0E' ? '#FFFFFF' : '#0E0E0E',
    },
    lineage: {
      hybrid: withContrast(colors.lineage?.hybrid?.main || ''),
      indica: withContrast(colors.lineage?.indica?.main || ''),
      sativa: withContrast(colors.lineage?.sativa?.main || ''),
      cbd: withContrast(colors.lineage?.cbd?.main || ''),
    },
  };
};

export const PremiumMenuEditor = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: bloomMenuData } = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenuData?.id
  );
  const [activeSection, setActiveSection] = useState<SidebarSection | null>(
    null
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const collapseSidebar = () => {
    setActiveSection(null);
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const expandSidebarWithSection = (section: SidebarSection) => {
    setSidebarCollapsed(false);
    setActiveSection(section);
  };

  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const { data: fontData, isPending: fontsLoading } = useGoogleFonts({
    apiKey: config.googleFontsKey,
  });

  // Mobile through iPad sizes (max-width: 1024px)
  const isCompactScreen = useDesktopMediaQuery({ width: 'max' });
  const isStackedView =
    !sidebarCollapsed && isCompactScreen && viewMode === 'desktop';
  const isTabletMobileView =
    !sidebarCollapsed && isCompactScreen && viewMode === 'mobile';

  const supportedFontFamilies = useMemo(() => fontData ?? [], [fontData]);
  const googleFontFromBloomFont = useCallback(
    (font_family?: string) => {
      return supportedFontFamilies.find(({ family }) => family === font_family);
    },
    [supportedFontFamilies]
  );

  const logoUrl = bloomMenuData?.logo?.large ?? undefined;

  const formValuesFromBloomMenu = useMemo<MenuComponentConfig>(
    () => ({
      category_tabs: bloomMenuData?.theme?.bloom?.category_tabs || {
        position: 'above_carousel',
        style: 'icon',
      },
      colors: {
        primary: {
          main: bloomMenuData?.theme?.bloom?.colors?.primary?.main,
        },
        secondary: {
          main: bloomMenuData?.theme?.bloom?.colors?.secondary?.main,
        },
        deals: {
          main: bloomMenuData?.theme?.bloom?.colors?.deals?.main,
        },
        header: {
          main: bloomMenuData?.theme?.bloom?.colors?.header?.main,
        },
        lineage: {
          hybrid: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.hybrid?.main,
          },
          indica: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.indica?.main,
          },
          sativa: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.sativa?.main,
          },
          cbd: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.cbd?.main,
          },
        },
      },
      fonts: {
        heading: googleFontFromBloomFont(
          bloomMenuData?.theme?.bloom?.fonts?.heading?.font_family
        ),
        body: googleFontFromBloomFont(
          bloomMenuData?.theme?.bloom?.fonts?.body?.font_family
        ),
      },
      logo: logoUrl,
      product_cards: bloomMenuData?.theme?.bloom?.product_cards || {
        card_size: 'medium',
        card_border: false,
        hover: 'shadow',
        cta_icon: 'add',
        cta_style: 'card_bottom_full',
      },
      search: bloomMenuData?.theme?.bloom?.search || {
        position: 'split',
      },
    }),
    [bloomMenuData, googleFontFromBloomFont]
  );

  const formMethods = useForm<MenuComponentConfig>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    values: formValuesFromBloomMenu,
    defaultValues: formValuesFromBloomMenu,
  });
  const {
    getValues,
    formState: { dirtyFields, isValid },
  } = formMethods;
  const formValues = getValues();

  const onPreview = useCallback(() => {
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuData?.theme?.bloom,
          category_tabs: formValues.category_tabs,
          colors: buildThemeColors(formValues.colors),
          fonts: {
            heading: formValues.fonts?.heading
              ? toBloomFontFamily(formValues.fonts.heading)
              : null,
            body: formValues.fonts?.body
              ? toBloomFontFamily(formValues.fonts.body)
              : null,
          },
          product_cards: formValues.product_cards,
          search: formValues.search,
        },
        // Only save logo if it was changed, otherwise it accidentally deletes the existing logo
        logo:
          dirtyFields['logo'] && formValues.logo ? formValues.logo : undefined,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuData?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuData.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [formValues, dirtyFields]);

  const onPublish = useCallback(
    (data: MenuComponentConfig) => {
      updateBloomMenu(
        {
          theme: {
            ...bloomMenuData?.theme?.bloom,
            category_tabs: data.category_tabs,
            colors: buildThemeColors(data.colors),
            fonts: {
              heading: data.fonts?.heading
                ? toBloomFontFamily(data.fonts.heading)
                : null,
              body: data.fonts?.body
                ? toBloomFontFamily(data.fonts.body)
                : null,
            },
            product_cards: data.product_cards,
            search: data.search,
          },
          // Only save logo if it was changed, otherwise it accidentally deletes the existing logo
          logo: dirtyFields['logo'] && data.logo ? data.logo : undefined,
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Menu updated!',
              variant: 'success',
            });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [bloomMenuData?.theme?.bloom, dirtyFields, navigate, toast, updateBloomMenu]
  );

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending || fontsLoading}
      onSubmit={onPublish}
      modalOverflow={isStackedView ? undefined : 'hidden'}
      centerContent={
        <Flex
          background="grays-white"
          borderRadius="sm"
          border="grays-light"
          p={4}
        >
          <Button
            className={clsx({
              [styles['viewMode_unselected']]: viewMode !== 'desktop',
            })}
            startIcon={<DesktopIcon />}
            label="Desktop"
            onClick={() => setViewMode('desktop')}
            variant={viewMode === 'desktop' ? 'primary' : 'tertiary'}
          />
          <Button
            className={clsx({
              [styles['viewMode_unselected']]: viewMode !== 'mobile',
            })}
            startIcon={<MobileIcon />}
            label="Mobile"
            onClick={() => setViewMode('mobile')}
            variant={viewMode === 'mobile' ? 'primary' : 'tertiary'}
          />
        </Flex>
      }
      preview={{
        onClick: onPreview,
        disable: !isValid || isPending,
        label: 'Preview',
      }}
      title="Menu editor"
    >
      <Flex
        p={40}
        gap={isStackedView ? 20 : 40}
        flexDirection={isStackedView ? 'column' : 'row'}
        height={isStackedView ? '100vh' : 'calc(100vh - 80px)'}
      >
        <EditorSidebar
          activeSection={activeSection}
          collapsed={sidebarCollapsed}
          collapseSidebar={collapseSidebar}
          expandSidebarWithSection={expandSidebarWithSection}
          isStackedView={isStackedView}
          isTabletMobileView={isTabletMobileView}
          setActiveSection={setActiveSection}
          supportedFontFamilies={supportedFontFamilies}
        />

        <EditorPreview
          activeSection={activeSection}
          sidebarCollapsed={sidebarCollapsed}
          isStackedView={isStackedView}
          isTabletMobileView={isTabletMobileView}
          viewMode={viewMode}
        />
      </Flex>
    </ModalPageWrapper>
  );
};
